import { hasWindow } from "@omni/utils/dom.utils";
import { retryWithDelay } from "@omni/utils/function.utils";
import { useEffect, useState } from "react";

export enum ScriptLoadingStates {
    Loading = "loading",
    Idle = "idle",
    Ready = "ready",
    Error = "error"
}

const useScript = (
    src: string,
    id: string,
    disabled?: boolean,
    configFn?: () => void
) => {
    const [status, setStatus] = useState(
        src ? ScriptLoadingStates.Loading : ScriptLoadingStates.Idle
    );

    const getScript = () => {
        return document.querySelector<HTMLScriptElement>(
            `script[src="${src}"]`
        );
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (disabled) return;

        // Allow falsy src value if waiting on other data needed for
        // constructing the script URL passed to this hook.
        if (!src || !hasWindow()) {
            setStatus(ScriptLoadingStates.Idle);
            return;
        }
        // Fetch existing script element by src
        // It may have been added by another intance of this hook
        let script = getScript();
        if (!script) {
            // execute config is provided
            if (configFn) {
                configFn();
            }
            // Create script
            script = document.createElement("script");
            script.src = src;
            script.id = id;
            script.async = true;
            script.setAttribute("data-status", ScriptLoadingStates.Loading);
            // Add script to document body
            document.body.appendChild(script);
            // Store status in attribute on script
            // This can be read by other instances of this hook
            const setAttributeFromEvent = (event: Event) => {
                script?.setAttribute(
                    "data-status",
                    event.type === "load"
                        ? ScriptLoadingStates.Ready
                        : ScriptLoadingStates.Error
                );
            };
            script.addEventListener("load", setAttributeFromEvent);
            script.addEventListener("error", setAttributeFromEvent);
        } else {
            // Grab existing script status from attribute and set to state.
            setStatus(
                script.getAttribute("data-status") as ScriptLoadingStates
            );
        }
        // Script event handler to update status in state
        // Note: Even if the script already exists we still need to add
        // event handlers to update the state for *this* hook instance.
        const setStateFromEvent = (event: Event) => {
            setStatus(
                event.type === "load"
                    ? ScriptLoadingStates.Ready
                    : ScriptLoadingStates.Error
            );
        };
        // Add event listeners
        script.addEventListener("load", setStateFromEvent);
        script.addEventListener("error", setStateFromEvent);
        // Remove event listeners on cleanup
        return () => {
            if (script) {
                script.removeEventListener("load", setStateFromEvent);
                script.removeEventListener("error", setStateFromEvent);
            }
        };
    }, [src, disabled]);

    const isScriptLoaded = async () => {
        if (!hasWindow()) return Promise.reject();
        await retryWithDelay({
            fn: async () => {
                const script = getScript();
                if (!script) {
                    throw new Error("Script not found");
                } else {
                    const status = script.getAttribute(
                        "data-status"
                    ) as ScriptLoadingStates;
                    if (status === ScriptLoadingStates.Ready) {
                        return await Promise.resolve();
                    } else {
                        throw new Error("Script not loaded");
                    }
                }
            }
        });
    };

    return { status, isScriptLoaded };
};

export { useScript };
